import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Briefcase,
  ExternalLink,
  Github,
  GraduationCap,
  Linkedin,
  Mail,
  Menu,
  Moon,
  Sun,
  Bot,
  Check,
  Copy,
  X,
} from 'lucide-react';

const portfolio = {
  name: 'Avaya Aggarwal',
  title: 'ML Engineer / Researcher',
  tagline: 'Building reliable multimodal systems and research tools.',
  summary:
    'I turn ambiguous ML ideas into testable systems: multimodal generation workflows, evaluation tooling, computer-vision research, and backend infrastructure. I care about the evidence behind a result as much as the implementation.',
  profileImage: '/home-hero-avatar.jpg',
  email: 'aggarwal.avaya27@gmail.com',
  socials: [
    {
      label: 'GitHub',
      href: 'https://github.com/OnePunchMonk',
      icon: Github,
    },
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/in/avaya-aggarwal/',
      icon: Linkedin,
    },
    {
      label: 'Kaggle',
      href: 'https://www.kaggle.com/avayaaggarwal27',
      icon: ExternalLink,
    },
  ],
  experience: [
    {
      role: 'ML Engineer Intern',
      company: 'Eternal',
      duration: 'Dec 2025 - Jun 2026',
      location: 'Gurugram, India',
      summary:
        'Built applied-AI and backend infrastructure for multimodal generation and AI platform operations.',
      bullets: [
        'Worked on the AI Gateway that connected the platform to language, image, and video models through a common interface for routing, validation, and model configuration.',
        'Built a multimodal marketing-content workflow that took a campaign brief through planning and review to avatar, voice, image, video, and post-production outputs.',
        'Added the supporting backend for asynchronous media processing, retries, persistent state, monitoring, and cost tracking.',
        'Worked on computer-vision and image-generation workflows for food, catalogue, and brand-personalisation use cases.',
        'Built Streamlit demos for creating and editing marketing content and reviewing costs.',
      ],
      stack: ['Python', 'LLM APIs', 'SQS', 'DynamoDB', 'FFmpeg'],
    },
    {
      role: 'Software Engineering Intern',
      company: 'Wells Fargo',
      duration: 'Jun 2025 - Aug 2025',
      location: 'Hyderabad, India',
      summary:
        'Built internal tooling around regression validation, mapping workflows, and GenAI-assisted analysis.',
      bullets: [
        'Reduced QA effort with a format-agnostic regression suite.',
        'Designed a configurable hierarchical JSON mapper backed by 40+ unit tests.',
        'Implemented a query-and-summary workflow for business users to speed up analysis.',
      ],
      stack: ['Python', 'Test Automation'],
    },
    {
      role: 'Software Engineering Intern',
      company: 'Ciena',
      duration: 'May 2024 - Jul 2024',
      location: 'Gurugram, India',
      summary:
        'Improved internal testing workflows for network systems using Python and APIs.',
      bullets: [
        'Revamped in-house Python libraries for 10G PON testing.',
        'Integrated REST APIs to improve maintainability and execution efficiency.',
      ],
      stack: ['Python', 'Test Automation'],
    }

  ],
  projects: [
    {
      title: 'erasus',
      category: 'Machine unlearning framework',
      description:
        'A machine unlearning framework built around coreset selection, multiple unlearning strategies, evaluation tooling, and certification workflows.',
      impact:
        'Includes 41 unlearning strategies, 24 selectors, benchmark pipelines, documentation, and a test suite with 465 passing tests.',
      stack: ['Python'],
      href: 'https://pypi.org/project/erasus/',
      imageUrl:
        'https://raw.githubusercontent.com/OnePunchMonk/erasus/main/benchmarks/tofu/results/coreset_ablation_gpu.png',
    },
    {
      title: 'AgentQuant',
      category: 'Agentic AI',
      description:
        'A failure-aware research harness for proposing, testing, and retaining lessons from quantitative-strategy experiments.',
      impact:
        'Built around a typed ReAct loop, SQLite-backed experiment memory, reproducible runs, and an inspectable offline demo.',
      stack: ['Python', 'LangGraph', 'SQLite'],
      href: 'https://github.com/OnePunchMonk/AgentQuant',
      imageUrl: '/projects/agentquant.jpg',
    },
    {
      title: 'TradeRecon',
      category: 'Trade reconciliation engine',
      description:
        'A Kafka-driven post-trade reconciliation engine that compares execution, broker confirmation, and PnL records in real time.',
      impact:
        'Flags price, quantity, timestamp, and PnL mismatches with SQLite audit trails, Prometheus metrics, Grafana dashboards, and HTML reports.',
      stack: ['Python', 'Kafka', 'SQLite', 'Prometheus'],
      href: 'https://github.com/OnePunchMonk/TradeRecon',
      imageUrl:
        '/projects/traderecon.jpg',
    },
    {
      title: 'KoRA',
      category: 'PEFT',
      description:
        'A research exploration of multiplicative inter-adapter communication for parameter-efficient fine-tuning.',
      impact:
        'Includes an explicit formal analysis, counterexamples, tests, and an implementation boundary between the proposal and validated behaviour.',
      stack: ['PyTorch', 'PEFT', 'Vision Models'],
      href: 'https://github.com/OnePunchMonk/KoRA',
      imageUrl:
        '/projects/kora.jpg',
    },
    {
      title: 'Pix Coreset',
      category: 'Coreset proof of concept',
      description:
        'An interactive coreset generation tool for computer vision experiments, packaged with a FastAPI backend and Streamlit interface.',
      impact:
        'Achieved 90% data compression for KNN segmentation workflows with Dockerized deployment, CI, and Pytest coverage.',
      stack: ['Python', 'Computer Vision', 'Docker', 'FastAPI', 'Pytest', 'Streamlit'],
      href: 'https://github.com/OnePunchMonk/PixCoreset',
      imageUrl: 'https://opengraph.githubassets.com/1/OnePunchMonk/PixCoreset',
    },
    {
      title: 'Forget Me Net',
      category: 'Unlearning proof of concept',
      description:
        'A verifiable machine unlearning prototype built around SISA training, model serving, and downloadable proof artifacts.',
      impact:
        'Reached 121-second shard-level unlearning and produced downloadable proof certificates backed by FastAPI, Streamlit, Transformers, and IPFS.',
      stack: ['Python', 'FastAPI', 'Streamlit', 'Transformers', 'IPFS'],
      href: 'https://github.com/OnePunchMonk/ForgetMeNet',
      imageUrl: 'https://opengraph.githubassets.com/1/OnePunchMonk/ForgetMeNet',
    },
    {
      title: 'uois_toolkit',
      category: 'Open-source computer vision toolkit',
      description:
        'A three-person collaboration with researchers at IRVL, UT Dallas: a unified PyTorch toolkit for unseen-object instance-segmentation datasets, loaders, sampling, augmentation, and evaluation.',
      collaborators: [
        {
          name: 'Jishnu Jayakumar',
          href: 'https://jishnujayakumar.github.io/',
        },
        {
          name: 'Animesh Maheshwari',
          href: 'https://animeshmaheshwari22.github.io/',
        },
      ],
      impact:
        'Standardizes five UOIS datasets behind one API, includes built-in research metrics, and shipped as an installable PyPI package.',
      stack: ['Python', 'PyTorch Lightning'],
      href: 'https://pypi.org/project/uois-toolkit/',
      imageUrl:
        'https://raw.githubusercontent.com/OnePunchMonk/uois_toolkit/main/media/banner.gif',
    },
    {
      title: 'Coresets for Image Segmentation',
      category: 'Hackathon project',
      description:
        'A coreset-driven image segmentation project focused on reducing training data while preserving segmentation quality.',
      impact:
        'Built as a focused project submission around coresets for image segmentation.',
      stack: ['Python', 'Machine Learning', 'Computer Vision', 'Segmentation'],
      href: 'https://github.com/OnePunchMonk/Coresets-for-Image-Segmentation',
      imageUrl:
        'https://opengraph.githubassets.com/1/OnePunchMonk/Coresets-for-Image-Segmentation',
    },
    {
      title: 'tiny-tribe',
      category: 'Brain encodings',
      description:
        'A lightweight distilled brain encoding model that predicts fMRI responses to naturalistic video from audio, visual, and text features.',
      impact:
        'Distills TRIBE v2 into a compact multimodal student model with about 14M trainable parameters and reported Pearson r of 0.7278 on the proof of concept.',
      stack: ['Python', 'PyTorch', 'Distillation', 'fMRI'],
      href: 'https://github.com/OnePunchMonk/tiny-tribe',
      imageUrl:
        '/projects/tiny-tribe.jpg',
    },
    {
      title: 'BTCTrade',
      category: 'Hackathon project',
      description:
        'A deep-learning trading bot that combines LSTM price forecasting with technical indicators including Bollinger Bands and moving averages.',
      impact:
        'Validated with backtesting, reporting RMSE of 417, Sharpe ratio of 8.71, and Sortino ratio of 2.47.',
      stack: ['Python', 'LSTM', 'Backtesting', 'Technical Indicators'],
      href: 'https://github.com/OnePunchMonk/BTCTrade',
      imageUrl: 'https://opengraph.githubassets.com/1/OnePunchMonk/BTCTrade',
    },
  ],
  specSheet: [
    { label: 'Status', value: 'Open to ML roles' },
    { label: 'Focus', value: 'Multimodal systems, evaluation' },
    { label: 'Now', value: 'Diffusion post-training' },
    { label: 'Recent', value: 'ML Engineer Intern, Eternal' },
    { label: 'Shipped', value: '2 PyPI packages' },
    { label: 'Based', value: 'Delhi, India' },
  ],
  featuredProjects: ['AgentQuant', 'TradeRecon', 'uois_toolkit', 'KoRA', 'tiny-tribe'],
  focusAreas: [
    {
      title: 'Build for inspection',
      description: 'Typed interfaces, tests, traces, and useful failure modes—not just a polished demo.',
    },
    {
      title: 'Evaluate before claiming',
      description: 'I prefer reproducible baselines, explicit assumptions, and honest boundaries around results.',
    },
    {
      title: 'Bridge research and systems',
      description: 'From model experiments to async services, observability, and workflows people can actually use.',
    },
  ],
  currentWork: [
    {
      title: 'Diffusion post-training and evaluation',
      description: 'Building reproducible workflows for LoRA, preference optimization, GRPO, and step distillation on open-weight diffusion models.',
      lookingFor: 'Feedback on evaluation design, reward quality, ablations, and reproducible baselines.',
      href: 'https://github.com/OnePunchMonk/diffusion-post-training-lab',
    },
  ],
  skills: [
    {
      group: 'Languages',
      items: ['Python', 'SQL'],
    },
    {
      group: 'ML and data',
      items: [
        'PyTorch',
        'TensorFlow',
        'Benchmarking and evaluation',
      ],
    },
    {
      group: 'Backend and infra',
      items: ['FastAPI', 'PostgreSQL', 'Docker', 'Kafka', 'REST APIs'],
    },
    {
      group: 'Engineering practice',
      items: ['Pytest', 'Regression testing', 'Research prototyping', 'System design'],
    },
  ],
  education: {
    school: 'Netaji Subhas University of Technology',
    degree: 'B.Tech in Computer Science Engineering with AI',
    duration: '2022 - 2026',
    coursework: [
      'Data Structures and Algorithms',
      'Machine Learning',
      'Artificial Intelligence',
      'Deep Learning',
      'Operating Systems',
      'Database Management Systems',
      'Game Theory',
    ],
  },
};

const sectionLabelClass =
  'font-mono text-xs font-semibold uppercase tracking-[0.16em] text-accent-700 dark:text-accent-300';
const sectionHeadingClass =
  'font-serif text-3xl font-semibold tracking-[-0.02em] text-neutral-950 dark:text-white sm:text-4xl';

const navigation = [
  { label: 'Work', href: '#work' },
  { label: 'Now', href: '#current-work' },
  { label: 'Industry Experience', href: '#experience' },
  { label: 'Skills', href: '#skills' },
];

const projectLookup = new Map(
  portfolio.projects.map((project) => [project.title, project])
);

const featuredProjects = portfolio.featuredProjects
  .map((title) => projectLookup.get(title))
  .filter(Boolean);

function SectionIntro({ label, title, body }) {
  return (
    <div className="max-w-2xl">
      {label ? <p className={sectionLabelClass}>{label}</p> : null}
      <h2 className={`${sectionHeadingClass} ${label ? 'mt-3' : ''}`}>{title}</h2>
      {body ? <p className="mt-4 text-base leading-8 text-neutral-600 dark:text-neutral-400">{body}</p> : null}
    </div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

function ProjectCard({ project }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.article
      initial={reduceMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.25 }}
      variants={fadeUp}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-neutral-200/90 bg-white/90 transition duration-300 hover:border-accent-500/50 dark:border-white/10 dark:bg-neutral-900/75 dark:hover:border-accent-300/40"
    >
      <div className={`aspect-[16/10] overflow-hidden border-b border-neutral-200 dark:border-white/10 ${project.imageFit === 'contain' ? 'bg-white p-4' : 'bg-neutral-100 dark:bg-neutral-800'}`}>
        <img
          src={project.imageUrl}
          alt={`${project.title} preview`}
          loading="lazy"
          className={`h-full w-full ${project.imageFit === 'contain' ? 'object-contain' : 'object-cover object-left-top'}`}
        />
      </div>
      <div className="flex flex-1 flex-col justify-between p-6">
        <div>
          <p className="font-mono text-xs text-neutral-500 dark:text-neutral-400">{project.category}</p>
          <h3 className="mt-2 text-2xl font-semibold tracking-[-0.025em] text-neutral-950 dark:text-white">
            <a
              href={project.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 transition-colors hover:text-accent-700 dark:hover:text-accent-300"
            >
              {project.title}
              <ArrowUpRight size={20} className="text-neutral-400" />
            </a>
          </h3>
          <p className="mt-4 text-sm leading-7 text-neutral-700 dark:text-neutral-300">
            {project.description}
            {project.collaborators ? (
              <>
                {' Co-built with '}
                {project.collaborators.map((collaborator, index) => (
                  <React.Fragment key={collaborator.href}>
                    {index > 0 ? ' and ' : ''}
                    <a
                      href={collaborator.href}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-accent-700 underline decoration-accent-500/40 underline-offset-4 transition hover:text-accent-900 dark:text-accent-300 dark:hover:text-accent-100"
                    >
                      {collaborator.name}
                    </a>
                  </React.Fragment>
                ))}
                .
              </>
            ) : null}
          </p>
          <p className="mt-4 text-sm leading-7 text-neutral-600 dark:text-neutral-400">{project.impact}</p>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          {project.stack.map((tech) => (
            <span
              key={tech}
              className="rounded-full border border-accent-600/20 bg-accent-50 px-3 py-1 font-mono text-xs font-medium text-accent-900 dark:border-accent-400/20 dark:bg-accent-400/10 dark:text-accent-100"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </motion.article>
  );
}

function ExperienceCard({ item }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.article
      initial={reduceMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeUp}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-lg border border-neutral-200 bg-white/90 p-6 dark:border-white/10 dark:bg-neutral-950/80 sm:pl-10 lg:p-8 lg:pl-12"
    >
      <div className="absolute left-[-3px] top-8 hidden h-2.5 w-2.5 rounded-full bg-accent-300 sm:block" />
      <div className="flex flex-col gap-6 lg:flex-row lg:justify-between">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 text-sm text-accent-700 dark:text-accent-300">
            <Briefcase size={16} />
            <span>{item.company}</span>
          </div>
          <h3 className="mt-3 text-2xl font-semibold text-neutral-950 dark:text-white">{item.role}</h3>
          <p className="mt-3 text-sm leading-7 text-neutral-600 dark:text-neutral-400">{item.summary}</p>
          <ul className="mt-5 space-y-3 text-sm leading-7 text-neutral-700 dark:text-neutral-300">
            {item.bullets.map((bullet) => (
              <li key={bullet} className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-300" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
          {item.links ? (
            <div className="mt-5 flex flex-wrap gap-3">
              {item.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-accent-700 underline decoration-accent-500/40 underline-offset-4 transition hover:text-accent-900 dark:text-accent-300 dark:hover:text-accent-100"
                >
                  Code: {link.label}
                  <ExternalLink size={14} />
                </a>
              ))}
            </div>
          ) : null}
        </div>

        <div className="w-full max-w-sm">
          <div className="border-t border-neutral-200 pt-4 dark:border-white/10 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <p className="font-mono text-sm font-medium text-neutral-950 dark:text-white">{item.duration}</p>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{item.location}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {item.stack.map((tech) => (
                <span
                  key={tech}
                  className="rounded-full bg-white px-3 py-1 font-mono text-xs text-neutral-700 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-white/10"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

const AGENT_ROUTE = '#/agent';

function useRoute() {
  const [route, setRoute] = useState(() => window.location.hash);
  useEffect(() => {
    const onChange = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

function AgentPage() {
  const [text, setText] = useState('');
  const [status, setStatus] = useState('loading');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch('/llms.txt')
      .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
      .then((body) => {
        setText(body);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <main id="main-content" className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
      <p className={sectionLabelClass}>For AI agents</p>
      <h1 className="mt-3 font-serif text-4xl font-semibold tracking-[-0.02em] text-neutral-950 dark:text-white sm:text-5xl">Hello, agent.</h1>
      <p className="mt-5 max-w-[60ch] text-base leading-8 text-neutral-600 dark:text-neutral-400">
        This is the whole site as plain Markdown, so you can skip the layout. Humans are welcome too. The raw file lives at{' '}
        <a href="/llms.txt" className="font-mono text-sm text-accent-700 underline decoration-accent-500/40 underline-offset-4 dark:text-accent-300">/llms.txt</a>.
      </p>
      <div className="mt-10 overflow-hidden rounded-lg border border-neutral-200 bg-white dark:border-white/10 dark:bg-neutral-900">
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-2 dark:border-white/10">
          <span className="font-mono text-xs text-neutral-500 dark:text-neutral-400">llms.txt</span>
          <div className="flex gap-1">
            <a href="/llms.txt" target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-white/5">
              Raw <ArrowUpRight size={13} />
            </a>
            <button type="button" onClick={copy} disabled={status !== 'ready'} className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 dark:text-neutral-300 dark:hover:bg-white/5">
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
        {status === 'loading' ? (
          <div className="space-y-3 p-5" aria-hidden="true">
            {[78, 56, 91, 64, 83, 47].map((w) => (
              <div key={w} className="h-3 animate-pulse rounded bg-neutral-100 dark:bg-white/5" style={{ width: `${w}%` }} />
            ))}
          </div>
        ) : status === 'error' ? (
          <p className="p-5 text-sm text-neutral-700 dark:text-neutral-300">
            The file didn't load here. Open <a href="/llms.txt" className="underline">/llms.txt</a> directly.
          </p>
        ) : (
          <pre className="overflow-x-auto whitespace-pre-wrap p-5 font-mono text-[13px] leading-6 text-neutral-800 dark:text-neutral-200">{text}</pre>
        )}
      </div>
      <a href="#top" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-950 dark:text-neutral-300 dark:hover:text-white">
        <ArrowRight size={15} className="rotate-180" /> Back to the site
      </a>
    </main>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isAgent = useRoute() === AGENT_ROUTE;
  const isDark = theme === 'dark';

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', isDark ? '#0f1114' : '#f8f7f4');
  }, [isDark]);

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    try { window.localStorage.setItem('portfolio-theme', next); } catch {}
    setTheme(next);
  };

  // The page renders client-side, so the browser's own jump to #section on load misses. Redo it once mounted.
  useEffect(() => {
    const { hash } = window.location;
    if (!hash || hash === AGENT_ROUTE) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target) return;
    // Wait for web fonts: re-wrapping text after they swap in would shift the target out of place.
    const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();
    fontsReady.then(() => requestAnimationFrame(() => target.scrollIntoView()));
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setIsMenuOpen(false); };
    const onResize = () => { if (window.innerWidth >= 768) setIsMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [isMenuOpen]);

  return (
    <div>
      <div className="relative min-h-screen overflow-x-clip bg-[#f8f7f4] text-neutral-800 transition-colors duration-300 dark:bg-[#0f1114] dark:text-neutral-200">

      <a href="#main-content" className="sr-only z-50 rounded-b-lg bg-neutral-950 px-4 py-3 text-sm font-semibold text-white focus:not-sr-only focus:absolute focus:left-5 focus:top-0">Skip to content</a>
      <header className="sticky top-0 z-40 border-b border-neutral-200/80 bg-[#f8f7f4]/80 backdrop-blur-xl dark:border-white/10 dark:bg-[#0f1114]/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 lg:px-8">
          <a href="#top" className="flex items-center gap-3 text-sm font-bold tracking-[-0.01em] text-neutral-900 dark:text-white">
            <span className="block h-8 w-8 overflow-hidden rounded-full border border-neutral-300 dark:border-white/15"><img src={portfolio.profileImage} alt="" className="h-full w-full origin-[58%_30%] scale-[2.2] object-cover object-top" /></span>
            <span>{portfolio.name}</span>
          </a>
          <div className="flex items-center gap-3">
          <nav className="hidden items-center gap-5 text-sm font-medium text-neutral-600 dark:text-neutral-300 md:flex">
            {navigation.map((item) => (
              <a key={item.href} href={item.href} className="transition hover:text-accent-700 dark:hover:text-accent-300">{item.label}</a>
            ))}
            <a href={AGENT_ROUTE} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 font-mono text-xs transition ${isAgent ? 'border-accent-600 text-accent-700 dark:border-accent-300 dark:text-accent-300' : 'border-neutral-300 text-neutral-600 hover:border-accent-600 hover:text-accent-700 dark:border-white/15 dark:text-neutral-300 dark:hover:border-accent-300 dark:hover:text-accent-300'}`}>
              <Bot size={14} /> Hi, agent
            </a>
            <a href="#contact" className="select-none rounded-md bg-neutral-950 px-4 py-2 text-white transition hover:bg-accent-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-accent-100">Contact</a>
          </nav>
          <button
            type="button"
            onClick={toggleTheme}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 transition hover:border-accent-500 hover:text-neutral-950 dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-accent-300 dark:hover:text-white"
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          >
            {isDark ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button type="button" onClick={() => setIsMenuOpen((open) => !open)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 md:hidden dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-200" aria-expanded={isMenuOpen} aria-controls="mobile-navigation" aria-label="Toggle navigation">
            {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          </div>
        </div>
        {isMenuOpen ? (
          <nav id="mobile-navigation" className="border-t border-neutral-200 bg-[#f8f7f4] px-5 py-4 dark:border-white/10 dark:bg-[#0f1114] md:hidden">
            <div className="mx-auto grid max-w-6xl gap-1">
              {[...navigation, { label: 'Hi, agent', href: AGENT_ROUTE }, { label: 'Contact', href: '#contact' }].map((item) => (
                <a key={item.href} href={item.href} onClick={() => setIsMenuOpen(false)} className="rounded-lg px-3 py-3 text-sm font-semibold text-neutral-800 transition hover:bg-accent-50 hover:text-accent-800 dark:text-neutral-200 dark:hover:bg-accent-400/10 dark:hover:text-accent-100">{item.label}</a>
              ))}
            </div>
          </nav>
        ) : null}
      </header>

      {isAgent ? <AgentPage /> : (
      <main id="main-content">
        <section id="top" className="mx-auto max-w-6xl px-5 pb-16 pt-14 lg:px-8 lg:pb-24 lg:pt-24">
          <div className="grid items-center gap-12 md:grid-cols-[1fr_auto] md:gap-16">
            <div className="max-w-3xl">
              <a
                href={`mailto:${portfolio.email}?subject=ML%20role%20opportunity`}
                className="mb-6 inline-flex items-center gap-2.5 rounded-lg border border-emerald-500/30 sm:rounded-full bg-emerald-50 px-4 py-2 text-sm text-emerald-900 transition hover:border-emerald-500/60 dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-100 dark:hover:border-emerald-400/50"
              >
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:animate-none" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span>
                  <span className="font-semibold">Looking for ML roles.</span> Feel free to reach out if you believe there's a fit.
                </span>
              </a>
              <h1 className="max-w-3xl font-serif leading-[1.1] text-4xl font-semibold tracking-[-0.03em] text-neutral-950 dark:text-white sm:text-5xl lg:text-6xl">
                {portfolio.title}
              </h1>
              <p className="mt-6 max-w-xl text-xl font-medium leading-8 text-neutral-800 dark:text-neutral-200 sm:text-2xl">{portfolio.tagline}</p>
              <p className="mt-6 max-w-2xl text-base leading-8 text-neutral-600 dark:text-neutral-400">
                {portfolio.summary}
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <a
                  href="#work"
                  className="inline-flex items-center gap-2 select-none rounded-md bg-neutral-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-accent-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-accent-100"
                >
                  View projects
                  <ArrowRight size={16} />
                </a>
                <a
                  href={`mailto:${portfolio.email}`}
                  className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white/70 px-5 py-3 text-sm font-semibold text-neutral-800 transition hover:border-accent-500 hover:text-neutral-950 dark:border-white/10 dark:bg-white/[0.04] dark:text-neutral-200 dark:hover:border-accent-300 dark:hover:text-white"
                >
                  <Mail size={16} />
                  Email
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                {portfolio.socials.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 py-2 text-sm text-neutral-600 transition hover:text-accent-700 dark:text-neutral-400 dark:hover:text-accent-300"
                    >
                      <Icon size={16} />
                      {social.label}
                    </a>
                  );
                })}
              </div>
            </div>

            <aside className="w-full overflow-hidden rounded-lg border border-neutral-300 bg-white text-sm dark:border-white/10 dark:bg-neutral-900 md:w-[22rem] md:justify-self-end">
              <img
                src={portfolio.profileImage}
                alt={portfolio.name}
                className="h-56 w-full rounded-t-lg border-b border-neutral-200 object-cover object-[50%_20%] md:aspect-[4/3] md:h-auto dark:border-white/10"
              />
              <dl className="divide-y divide-neutral-200 dark:divide-white/10">
                {portfolio.specSheet.map((row) => (
                  <div key={row.label} className="grid grid-cols-[6.5rem_1fr] gap-3 px-5 py-3">
                    <dt className="pt-0.5 font-mono text-xs uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">{row.label}</dt>
                    <dd className="font-medium text-neutral-900 dark:text-neutral-100">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </aside>
          </div>
        </section>

        <section id="work" className="mx-auto max-w-6xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionIntro
            label="Selected work"
            title="Code, experiments, and systems"
            body="A small, representative set of public work. Each card links directly to the implementation or package—not a case-study landing page."
          />

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featuredProjects.map((project) => (
              <ProjectCard key={project.title} project={project} />
            ))}
          </div>
          <a
            href="https://github.com/OnePunchMonk?tab=repositories"
            target="_blank"
            rel="noreferrer"
            className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-accent-700 transition hover:text-accent-950 dark:text-accent-300 dark:hover:text-white"
          >
            Browse all public repositories <ExternalLink size={15} />
          </a>
        </section>

        <section className="border-y border-neutral-200 bg-white/55 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="mx-auto max-w-6xl px-5 py-20 lg:px-8 lg:py-24">
            <SectionIntro
              title="How I like to work"
              body="The thread through my projects is making technical work legible: what was built, how it was checked, and where the result stops being certain."
            />
            <div className="mt-10 divide-y divide-neutral-200 dark:divide-white/10">
              {portfolio.focusAreas.map((area, index) => (
                <div
                  key={area.title}
                  className="grid gap-3 py-6 first:pt-0 last:pb-0 sm:grid-cols-[auto_1fr_1.4fr] sm:items-baseline sm:gap-8"
                >
                  <span className="font-mono text-sm text-accent-700 dark:text-accent-300">0{index + 1}</span>
                  <h3 className="text-xl font-semibold text-neutral-950 dark:text-white">{area.title}</h3>
                  <p className="text-sm leading-7 text-neutral-600 dark:text-neutral-400">{area.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="current-work" className="border-y border-neutral-200 bg-accent-50/45 dark:border-white/10 dark:bg-accent-400/[0.04]">
          <div className="mx-auto max-w-6xl px-5 py-20 lg:px-8 lg:py-24">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <SectionIntro title="Open threads and collaboration opportunities" body="These are active areas where useful technical feedback, collaboration, or a careful second pair of eyes would help." />
              <a href={`mailto:${portfolio.email}?subject=Idea%20or%20collaboration%20for%20Avaya%27s%20ML%20systems%20work`} className="inline-flex shrink-0 items-center gap-2 self-start select-none rounded-md bg-neutral-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-accent-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-accent-100 lg:self-auto">
                Share an idea <ArrowRight size={16} />
              </a>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {portfolio.currentWork.map((item) => (
                <article key={item.title} className="rounded-lg border border-accent-900/10 bg-white/85 p-7 transition dark:border-accent-100/10 dark:bg-neutral-950/70">
                  <h3 className="text-xl font-semibold text-neutral-950 dark:text-white">{item.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-neutral-700 dark:text-neutral-300">{item.description}</p>
                  <p className="mt-4 text-sm leading-7 text-neutral-600 dark:text-neutral-400"><span className="font-semibold text-neutral-900 dark:text-neutral-200">Looking for:</span> {item.lookingFor}</p>
                  <a href={item.href} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-accent-700 hover:text-accent-950 dark:text-accent-300 dark:hover:text-white">Explore the work <ExternalLink size={14} /></a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="experience" className="border-y border-neutral-200 bg-white/55 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="mx-auto max-w-6xl px-6 py-16 lg:px-8 lg:py-24">
            <div className="grid gap-12 lg:grid-cols-[0.72fr_1.28fr]">
              <div>
                <SectionIntro
                  label="Industry Experience"
                  title="Roles"
                  body="Most of my work has been in environments where the technical problem needed both experimentation and implementation discipline."
                />
              </div>

              <div className="relative">
                <div className="absolute left-2 top-2 hidden h-[calc(100%-1rem)] w-px bg-neutral-200 dark:bg-white/10 sm:block" />
                <div className="space-y-6">
                  {portfolio.experience.map((item) => (
                    <ExperienceCard key={`${item.company}-${item.role}`} item={item} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="skills" className="mx-auto max-w-6xl px-6 py-16 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr]">
            <div>
              <SectionIntro
                title="Tools and systems"
                body="The stack I use most often."
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {portfolio.skills.map((group) => (
                <div
                  key={group.group}
                  className="rounded-lg border border-neutral-200 bg-white/90 p-6 dark:border-white/10 dark:bg-neutral-900/75"
                >
                  <h3 className="text-lg font-semibold text-neutral-950 dark:text-white">{group.group}</h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {group.items.map((item) => (
                      <span
                        key={item}
                        className="rounded-full border border-neutral-200 px-3 py-1 font-mono text-sm text-neutral-700 dark:border-white/10 dark:text-neutral-300"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-neutral-200 bg-white/55 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="mx-auto grid max-w-6xl gap-6 px-6 py-16 lg:grid-cols-[0.7fr_1.3fr] lg:px-8 lg:py-24">
            <div>
              <SectionIntro title="Academic background" />
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white/90 p-8 dark:border-white/10 dark:bg-neutral-950/80">
              <div className="flex items-center gap-3 text-sm text-accent-700 dark:text-accent-300">
                <GraduationCap size={18} />
                {portfolio.education.school}
              </div>
              <h3 className="mt-3 text-2xl font-semibold text-neutral-950 dark:text-white">{portfolio.education.degree}</h3>
              <p className="mt-2 font-mono text-sm text-neutral-600 dark:text-neutral-400">{portfolio.education.duration}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {portfolio.education.coursework.map((course) => (
                  <span
                    key={course}
                    className="rounded-full border border-neutral-200 px-3 py-1 font-mono text-sm text-neutral-700 dark:border-white/10 dark:text-neutral-300"
                  >
                    {course}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="border-t border-neutral-200 bg-white/55 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="mx-auto grid max-w-6xl gap-6 px-6 py-16 lg:grid-cols-[1fr_auto] lg:items-end lg:px-8 lg:py-20">
            <div>
              <p className={sectionLabelClass}>Contact</p>
              <h2 className="mt-3 max-w-2xl font-serif text-3xl font-semibold leading-snug text-neutral-950 dark:text-white sm:text-4xl">
                Open to ML engineering, computer vision, and research-facing roles
              </h2>
            </div>

            <div className="flex flex-wrap gap-3 lg:justify-end">
              <a
                href={`mailto:${portfolio.email}`}
                className="inline-flex items-center gap-2 select-none rounded-md bg-neutral-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-accent-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-accent-100"
              >
                <Mail size={16} />
                {portfolio.email}
              </a>
            </div>
          </div>

          <footer className="mx-auto flex max-w-6xl flex-col gap-5 border-t border-neutral-200 px-6 py-6 dark:border-white/10 md:flex-row md:items-center md:justify-between lg:px-8">
            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              <span className="font-medium text-neutral-900 dark:text-neutral-300">{portfolio.name}</span>
            </div>

            <div className="flex flex-wrap gap-3">
              {portfolio.socials.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-4 py-2 text-sm text-neutral-700 transition hover:border-neutral-400 hover:text-neutral-950 dark:border-white/10 dark:text-neutral-300 dark:hover:border-white/25 dark:hover:text-white"
                  >
                    <Icon size={15} />
                    {social.label}
                  </a>
                );
              })}
            </div>
          </footer>
        </section>
      </main>
      )}
      </div>
    </div>
  );
}

export default App;
